import { fetchDraftPreviewXml } from './api';

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Renders a watermarked dry-run PDF preview of a draft and downloads it, mirroring the KSeF-synced
 * `generateInvoicePdf` path (a `link.download` anchor).
 *
 * The server owns the authoritative FA(3) render: `flush()` persists the current edits, then
 * `GET /{id}/preview-xml` returns XML produced by the real `Fa3XmlWriter` (and re-validated against the
 * XSD). We only turn that XML into a visual PDF via the vendored converter — passing an empty `nrKSeF`
 * (truthy-guarded, so the KSeF row is omitted rather than throwing), no `qrCode`, and a preview
 * `watermark`. Throws `ApiError` (`code === 'DRAFT_NOT_RENDERABLE'` on 422) when the draft can't
 * assemble; the caller toasts.
 *
 * We download rather than open in a tab: the previous open-blank-tab-then-set-location approach
 * rendered a blank tab in Chromium/mobile (navigating an `about:blank` popup to a `blob:` URL), and the
 * KSeF PDF path already downloads, so this keeps both PDF actions consistent and quirk-free.
 */
export async function previewDraftPdf(params: {
  id: number;
  watermark: string;
  flush: () => Promise<void>;
}): Promise<void> {
  await params.flush();
  const xml = await fetchDraftPreviewXml(params.id);

  // Loaded on demand: the generator bundles pdfmake + fonts (~1.5 MB), kept out of the initial bundle.
  const { generateInvoice } = await import('@akmf/ksef-fe-invoice-converter');
  const file = new File([xml], `draft-${params.id}.xml`, { type: 'application/xml' });
  const pdf = await generateInvoice(file, { nrKSeF: '', watermark: params.watermark }, 'blob');

  downloadBlob(pdf, `draft-${params.id}.pdf`);
}

/**
 * Downloads the draft's authoritative FA(3) XML — the same `GET /{id}/preview-xml` the PDF preview
 * renders from. `flush()` persists pending edits first so the file reflects the saved row. Throws
 * `ApiError` (`code === 'DRAFT_NOT_RENDERABLE'` on 422) when the draft can't assemble; the caller toasts.
 */
export async function downloadDraftXml(params: {
  id: number;
  flush: () => Promise<void>;
}): Promise<void> {
  await params.flush();
  const xml = await fetchDraftPreviewXml(params.id);
  downloadBlob(xml, `draft-${params.id}.xml`);
}
